// FilterRail — left filter sidebar (Apollo/Attio pattern), house style.
import { COUNTRIES } from './countries';
// Expandable sections, type-appropriate controls, sticky applied-filter summary,
// live match count, saved views. Replaces the dropdown FilterBar.
//
// Props:
//   schema, value (tree), onChange(tree), matchCount, loading
//   savedViews, onSaveView(name,shared), onApplyView(view), onDeleteView(id)
import React, { useState, useRef, useEffect } from 'react';
import { DATE_PRESETS } from './filterSchema';

const CRIMSON = '#8B1A1A';
const HAIR = '#E8E4E0';
const SEL_BG = '#FFF5F5';
const SEL_FG = '#C53030';
const INK = '#1A1A1A';
const MUTE = '#9A938B';
const PANEL_BG = '#FAF8F5';

function leavesOf(tree) { return (tree && Array.isArray(tree.and)) ? tree.and : []; }
function treeFrom(leaves) { return leaves.length ? { and: leaves } : null; }

function ctrlOf(field) {
  if (field.control) return field.control;
  switch (field.type) {
    case 'text': return 'tokens';
    case 'number': return 'range';
    case 'date': return 'date';
    case 'enum': return 'multi';
    case 'boolean': return 'bool';
    case 'exists': return 'bool';
    default: return 'text';
  }
}

function describe(field, leaf) {
  const L = field.label;
  const ctrl = ctrlOf(field);
  if (ctrl === 'bool') return `${L}: ${leaf.value === false ? 'no' : 'yes'}`;
  if (ctrl === 'range') {
    if (leaf.op === 'is_null') return `${L}: none`;
    const lo = leaf.min, hi = leaf.max;
    if (lo != null && hi != null) return `${L} ${lo}–${hi}`;
    if (lo != null) return `${L} ≥ ${lo}`;
    if (hi != null) return `${L} ≤ ${hi}`;
    return L;
  }
  if (ctrl === 'multi') {
    const opts = (field.options || []).filter(o => (leaf.values || []).includes(o.value)).map(o => o.label);
    return `${L}: ${opts.join(', ') || '—'}`;
  }
  if (ctrl === 'date') {
    const p = DATE_PRESETS.find(d => d.value === leaf.value);
    return `${L} ${p ? p.label.toLowerCase() : leaf.value + ' days'}`;
  }
  if (ctrl === 'tokens') return `${L}: ${(leaf.values || []).join(' / ')}`;
  if (ctrl === 'text') return `${L}: "${leaf.value}"`;
  return L;
}

// which fields are "primary" (shown before Show more) — first N per group
const PRIMARY_PER_GROUP = 4;

export default function FilterRail({ schema, value, onChange, matchCount, loading, savedViews = [], onSaveView, onApplyView, onDeleteView, targets = {}, distincts = {} }) {
  const leaves = leavesOf(value);
  const fieldByKey = Object.fromEntries(schema.fields.map(f => [f.key, f]));
  const [open, setOpen] = useState(() => Object.fromEntries(schema.groups.map((g, i) => [g, i === 0])));
  const [showAll, setShowAll] = useState({});
  const [viewsOpen, setViewsOpen] = useState(false);
  const [saveOpen, setSaveOpen] = useState(false);
  const [saveName, setSaveName] = useState('');
  const [countryQuery, setCountryQuery] = useState({});
  // relation targets (campaigns etc.) are supplied by the parent via `targets` prop.
  const viewsRef = useRef(null);

  useEffect(() => {
    function onDoc(e) { if (viewsRef.current && !viewsRef.current.contains(e.target)) setViewsOpen(false); }
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  function setLeaf(field, leaf) {
    const next = leaves.filter(l => l.field !== field.key);
    if (leaf) next.push({ field: field.key, ...leaf });
    onChange(treeFrom(next));
  }
  function removeLeaf(key) { onChange(treeFrom(leaves.filter(l => l.field !== key))); }
  function clearAll() { onChange(null); }

  // Scoped-association leaves carry both a `target` and a condition; merge so editing one keeps the other.
  function patchLeaf(field, patch) {
    const existing = leaves.find(l => l.field === field.key) || {};
    const merged = { ...existing, ...patch, field: field.key };
    // if patch nulls the condition AND there's no target, drop the leaf
    const hasCond = merged.op != null;
    const next = leaves.filter(l => l.field !== field.key);
    if (hasCond) next.push(merged);
    onChange(treeFrom(next));
  }

  function targetsFor(relation) { return targets[relation] || []; }

  // Control is derived from the field's TYPE (single source of truth, mirrors server registry).
  // text -> tokens, number -> range, date -> date presets, enum -> multi, boolean/exists -> bool.
  function controlFor(field) {
    if (field.control) return field.control; // explicit override wins
    switch (field.type) {
      case 'text': return 'tokens';
      case 'number': return 'range';
      case 'date': return 'date';
      case 'enum': return 'multi';
      case 'boolean': return 'bool';
      case 'exists': return 'bool';
      default: return 'text';
    }
  }

  function control(field) {
    const leaf = leaves.find(l => l.field === field.key);
    const ctrl = controlFor(field);
    if (field.relation) {
      const leaf = leaves.find(l => l.field === field.key);
      const tgts = targetsFor(field.relation);
      const curTarget = leaf?.target ?? '';
      // condition control depends on field type, but emits via patchLeaf to preserve target
      const emitCond = (cond) => {
        if (!cond) { // clearing condition: if a target is set keep membership, else drop
          patchLeaf(field, { op: undefined, value: undefined, values: undefined, min: undefined, max: undefined });
          return;
        }
        patchLeaf(field, cond);
      };
      let condControl;
      if (ctrl === 'bool') {
        const v = leaf?.op ? (leaf.value === false ? 'no' : 'yes') : '';
        condControl = (
          <div style={{ display: 'flex', gap: 6 }}>
            {['yes','no'].map(opt => (
              <button key={opt} onClick={() => emitCond(v === opt ? null : { op: 'eq', value: opt === 'yes' })} style={seg(v === opt)}>{opt === 'yes' ? 'Yes' : 'No'}</button>
            ))}
          </div>
        );
      } else if (ctrl === 'multi') {
        const vals = leaf?.values || [];
        condControl = (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            {(field.options || []).map(o => {
              const on = vals.includes(o.value);
              return (
                <label key={o.value} onClick={() => { const nv = on ? vals.filter(x => x !== o.value) : [...vals, o.value]; emitCond(nv.length ? { op: 'in', values: nv } : null); }} style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', padding: '0.15rem 0' }}>
                  <span style={{ width: 15, height: 15, borderRadius: 4, border: `1.5px solid ${on ? CRIMSON : '#CFC8C0'}`, background: on ? CRIMSON : '#fff', flexShrink: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>{on && <span style={{ color: '#fff', fontSize: 10, lineHeight: 1 }}>✓</span>}</span>
                  <span style={{ fontSize: '0.8rem', color: '#4A5568' }}>{o.label}</span>
                </label>
              );
            })}
          </div>
        );
      } else if (ctrl === 'range') {
        const lo = leaf?.min ?? '', hi = leaf?.max ?? '';
        const upd = (which, val) => { const min = which === 'min' ? val : lo, max = which === 'max' ? val : hi; const m = min === '' ? null : Number(min), x = max === '' ? null : Number(max); emitCond((m == null && x == null) ? null : { op: 'between', min: m, max: x }); };
        condControl = (
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <input type="number" placeholder={String(field.min)} value={lo} onChange={e => upd('min', e.target.value)} style={numIn} />
            <span style={{ color: MUTE, fontSize: '0.75rem' }}>–</span>
            <input type="number" placeholder={String(field.max)} value={hi} onChange={e => upd('max', e.target.value)} style={numIn} />
          </div>
        );
      } else if (ctrl === 'date') {
        const cur = leaf?.value;
        condControl = (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
            {DATE_PRESETS.map(p => (<button key={p.value} onClick={() => emitCond(cur === p.value ? null : { op: 'within_days', value: p.value })} style={seg(cur === p.value)}>{p.label}</button>))}
          </div>
        );
      }
      return (
        <div>
          <select value={curTarget} onChange={e => patchLeaf(field, { target: e.target.value === '' ? undefined : Number(e.target.value), op: leaf?.op || 'eq', value: leaf?.value ?? true })}
            style={{ ...numIn, width: '100%', marginBottom: 6 }}>
            <option value="">Any {field.relation}</option>
            {tgts.map(t => <option key={t.value} value={t.value}>{t.label}{t.meta ? ` (${t.meta})` : ''}</option>)}
          </select>
          {condControl}
        </div>
      );
    }
    if (field.dropdown || field.dropdownOptions) {
      const vals = leaf?.values || [];
      const opts = (field.dropdownOptions || distincts[field.dropdown] || []).map(v => (v && typeof v === 'object' ? { value: v.value, label: v.label } : { value: v, label: v }));
      const q = (countryQuery[field.key] || '').toLowerCase();
      const shown = q ? opts.filter(o => o.label.toLowerCase().includes(q)) : opts.slice(0, 10);
      const toggle = (val) => {
        const nv = vals.includes(val) ? vals.filter(x => x !== val) : [...vals, val];
        setLeaf(field, nv.length ? { op: 'contains_any', values: nv } : null);
      };
      return (
        <div>
          <input type="text" placeholder={`Search ${field.label.toLowerCase()}…`} value={countryQuery[field.key] || ''}
            onChange={e => setCountryQuery(s => ({ ...s, [field.key]: e.target.value }))} style={{ ...numIn, width: '100%' }} />
          {vals.length > 0 && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 5 }}>
              {vals.map(v => (
                <span key={v} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, background: SEL_BG, border: '0.5px solid #FEB2B2', borderRadius: 6, padding: '0.1rem 0.25rem 0.1rem 0.45rem', fontSize: '0.72rem', color: SEL_FG, fontWeight: 500 }}>
                  {v}<button onClick={() => toggle(v)} style={{ border: 'none', background: 'none', color: SEL_FG, cursor: 'pointer', fontSize: '0.8rem', lineHeight: 1, padding: 0 }}>×</button>
                </span>
              ))}
            </div>
          )}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 2, marginTop: 5, maxHeight: 150, overflowY: 'auto' }}>
            {shown.map(o => {
              const on = vals.includes(o.value);
              return (
                <label key={o.value} onClick={() => toggle(o.value)} style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', padding: '0.12rem 0' }}>
                  <span style={{ width: 15, height: 15, borderRadius: 4, border: `1.5px solid ${on ? CRIMSON : '#CFC8C0'}`, background: on ? CRIMSON : '#fff', flexShrink: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>{on && <span style={{ color: '#fff', fontSize: 10, lineHeight: 1 }}>✓</span>}</span>
                  <span style={{ fontSize: '0.8rem', color: '#4A5568' }}>{o.label}</span>
                </label>
              );
            })}
            {opts.length === 0 && <span style={{ fontSize: '0.7rem', color: MUTE, paddingTop: 2 }}>No values yet</span>}
          </div>
        </div>
      );
    }
    if (field.countryMulti) {
      const vals = leaf?.values || [];
      const q = (countryQuery[field.key] || '').toLowerCase();
      const shown = q ? COUNTRIES.filter(o => o.label.toLowerCase().includes(q)) : COUNTRIES.slice(0, 8);
      const toggle = (val) => {
        const nv = vals.includes(val) ? vals.filter(x => x !== val) : [...vals, val];
        setLeaf(field, nv.length ? { op: 'contains_any', values: nv } : null);
      };
      return (
        <div>
          <input type="text" placeholder="Search countries…" value={countryQuery[field.key] || ''}
            onChange={e => setCountryQuery(s => ({ ...s, [field.key]: e.target.value }))} style={{ ...numIn, width: '100%' }} />
          {vals.length > 0 && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 5 }}>
              {vals.map(v => (
                <span key={v} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, background: SEL_BG, border: '0.5px solid #FEB2B2', borderRadius: 6, padding: '0.1rem 0.25rem 0.1rem 0.45rem', fontSize: '0.72rem', color: SEL_FG, fontWeight: 500 }}>
                  {v}<button onClick={() => toggle(v)} style={{ border: 'none', background: 'none', color: SEL_FG, cursor: 'pointer', fontSize: '0.8rem', lineHeight: 1, padding: 0 }}>×</button>
                </span>
              ))}
            </div>
          )}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 2, marginTop: 5, maxHeight: 150, overflowY: 'auto' }}>
            {shown.map(o => {
              const on = vals.includes(o.value);
              return (
                <label key={o.value} onClick={() => toggle(o.value)} style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', padding: '0.12rem 0' }}>
                  <span style={{ width: 15, height: 15, borderRadius: 4, border: `1.5px solid ${on ? CRIMSON : '#CFC8C0'}`, background: on ? CRIMSON : '#fff', flexShrink: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
                    {on && <span style={{ color: '#fff', fontSize: 10, lineHeight: 1 }}>✓</span>}
                  </span>
                  <span style={{ fontSize: '0.8rem', color: '#4A5568' }}>{o.label}</span>
                </label>
              );
            })}
            {!q && <span style={{ fontSize: '0.7rem', color: MUTE, paddingTop: 2 }}>Type to search all countries…</span>}
          </div>
        </div>
      );
    }
    if (ctrl === 'bool') {
      const v = leaf ? (leaf.value === false ? 'no' : 'yes') : '';
      // exists fields send eq true/false; column-backed booleans send not_null/is_null.
      const eqStyle = field.type === 'exists';
      return (
        <div style={{ display: 'flex', gap: 6 }}>
          {['yes','no'].map(opt => (
            <button key={opt} onClick={() => setLeaf(field, v === opt ? null : (eqStyle ? { op: 'eq', value: opt === 'yes' } : { op: opt === 'yes' ? 'not_null' : 'is_null', value: opt === 'yes' }))}
              style={seg(v === opt)}>{opt === 'yes' ? 'Yes' : 'No'}</button>
          ))}
        </div>
      );
    }
    if (ctrl === 'multi') {
      const vals = leaf?.values || [];
      return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          {field.options.map(o => {
            const on = vals.includes(o.value);
            return (
              <label key={o.value} style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', padding: '0.15rem 0' }}>
                <span onClick={() => { const nv = on ? vals.filter(x => x !== o.value) : [...vals, o.value]; setLeaf(field, nv.length ? { op: 'in', values: nv } : null); }}
                  style={{ width: 15, height: 15, borderRadius: 4, border: `1.5px solid ${on ? CRIMSON : '#CFC8C0'}`, background: on ? CRIMSON : '#fff', flexShrink: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
                  {on && <span style={{ color: '#fff', fontSize: 10, lineHeight: 1 }}>✓</span>}
                </span>
                <span style={{ fontSize: '0.8rem', color: '#4A5568' }}>{o.label}</span>
              </label>
            );
          })}
        </div>
      );
    }
    if (ctrl === 'range') {
      const lo = leaf?.min ?? '', hi = leaf?.max ?? '';
      const upd = (which, val) => {
        const min = which === 'min' ? val : lo, max = which === 'max' ? val : hi;
        const m = min === '' ? null : Number(min), x = max === '' ? null : Number(max);
        if (m == null && x == null) setLeaf(field, null); else setLeaf(field, { op: 'between', min: m, max: x });
      };
      return (
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <input type="number" placeholder={String(field.min)} value={lo} onChange={e => upd('min', e.target.value)} style={numIn} />
          <span style={{ color: MUTE, fontSize: '0.75rem' }}>–</span>
          <input type="number" placeholder={String(field.max)} value={hi} onChange={e => upd('max', e.target.value)} style={numIn} />
        </div>
      );
    }
    if (ctrl === 'date') {
      const cur = leaf?.value;
      return (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
          {DATE_PRESETS.map(p => (
            <button key={p.value} onClick={() => setLeaf(field, cur === p.value ? null : { op: 'within_days', value: p.value })} style={seg(cur === p.value)}>{p.label}</button>
          ))}
        </div>
      );
    }
    if (ctrl === 'tokens') {
      const vals = leaf?.values || [];
      const addToken = (raw) => {
        const parts = String(raw).split(',').map(s => s.trim()).filter(Boolean);
        if (!parts.length) return;
        const next = Array.from(new Set([...vals, ...parts]));
        setLeaf(field, { op: 'contains_any', values: next });
      };
      const removeToken = (v) => {
        const next = vals.filter(x => x !== v);
        setLeaf(field, next.length ? { op: 'contains_any', values: next } : null);
      };
      return (
        <div>
          <input type="text" placeholder="Type a term, Enter to add…"
            onKeyDown={e => { if (e.key === 'Enter' || e.key === ',') { e.preventDefault(); addToken(e.target.value); e.target.value = ''; } }}
            onBlur={e => { if (e.target.value.trim()) { addToken(e.target.value); e.target.value = ''; } }}
            style={{ ...numIn, width: '100%' }} />
          {vals.length > 0 && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 5 }}>
              {vals.map(v => (
                <span key={v} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, background: SEL_BG, border: '0.5px solid #FEB2B2', borderRadius: 6, padding: '0.1rem 0.25rem 0.1rem 0.45rem', fontSize: '0.72rem', color: SEL_FG, fontWeight: 500 }}>
                  {v}
                  <button onClick={() => removeToken(v)} style={{ border: 'none', background: 'none', color: SEL_FG, cursor: 'pointer', fontSize: '0.8rem', lineHeight: 1, padding: 0 }}>×</button>
                </span>
              ))}
            </div>
          )}
        </div>
      );
    }
    // text
    return (
      <input type="text" placeholder="Type to filter…" value={leaf?.value || ''}
        onChange={e => setLeaf(field, e.target.value ? { op: 'contains', value: e.target.value } : null)} style={{ ...numIn, width: '100%' }} />
    );
  }

  return (
    <div style={{ width: 280, flexShrink: 0, fontFamily: 'Inter, system-ui, sans-serif' }}>
      <div style={{ position: 'sticky', top: 12, border: `0.5px solid ${HAIR}`, borderRadius: 12, background: '#fff', overflow: 'hidden' }}>
        {/* header: saved views */}
        <div style={{ padding: '0.75rem 0.85rem', borderBottom: `0.5px solid ${HAIR}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between', position: 'relative' }} ref={viewsRef}>
          <span style={{ fontSize: '0.8rem', fontWeight: 600, color: INK }}>Filters</span>
          <button onClick={() => setViewsOpen(o => !o)} style={{ ...ghostBtn, fontSize: '0.74rem' }}>Views ▾</button>
          {viewsOpen && (
            <div style={{ position: 'absolute', top: '100%', right: 8, zIndex: 40, background: '#fff', border: `0.5px solid ${HAIR}`, borderRadius: 10, boxShadow: '0 6px 20px rgba(0,0,0,0.1)', minWidth: 220, padding: '0.25rem 0' }}>
              {savedViews.length === 0 && <div style={{ padding: '0.5rem 0.75rem', color: MUTE, fontSize: '0.76rem' }}>No saved views yet</div>}
              {savedViews.map(v => (
                <div key={v.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0.4rem 0.75rem' }}
                  onMouseEnter={e => e.currentTarget.style.background = PANEL_BG} onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                  <span onClick={() => { onApplyView && onApplyView(v); setViewsOpen(false); }} style={{ fontSize: '0.8rem', color: INK, flex: 1, cursor: 'pointer' }}>
                    {v.name}{v.shared ? <span style={{ color: MUTE, fontSize: '0.68rem', marginLeft: 5 }}>· shared</span> : ''}
                  </span>
                  <button onClick={() => onDeleteView && onDeleteView(v.id)} style={{ border: 'none', background: 'none', color: MUTE, cursor: 'pointer', fontSize: '0.9rem' }}>×</button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* applied summary (sticky readout) */}
        {leaves.length > 0 && (
          <div style={{ padding: '0.6rem 0.85rem', background: PANEL_BG, borderBottom: `0.5px solid ${HAIR}` }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
              <span style={{ fontSize: '0.72rem', color: MUTE }}>{loading ? 'Filtering…' : (typeof matchCount === 'number' ? `${matchCount} match${matchCount === 1 ? '' : 'es'}` : `${leaves.length} active`)}</span>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                {onSaveView && <button onClick={() => setSaveOpen(o => !o)} style={{ border: 'none', background: 'none', color: CRIMSON, cursor: 'pointer', fontSize: '0.72rem', fontWeight: 600 }}>Save view</button>}
                <button onClick={clearAll} style={{ border: 'none', background: 'none', color: CRIMSON, cursor: 'pointer', fontSize: '0.72rem', textDecoration: 'underline' }}>Clear all</button>
              </div>
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
              {leaves.map(l => {
                const f = fieldByKey[l.field]; if (!f) return null;
                return (
                  <span key={l.field} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, background: SEL_BG, border: `0.5px solid #FEB2B2`, borderRadius: 6, padding: '0.15rem 0.3rem 0.15rem 0.5rem', fontSize: '0.72rem', color: SEL_FG, fontWeight: 500 }}>
                    {describe(f, l)}
                    <button onClick={() => removeLeaf(l.field)} style={{ border: 'none', background: 'none', color: SEL_FG, cursor: 'pointer', fontSize: '0.85rem', lineHeight: 1, padding: 0 }}>×</button>
                  </span>
                );
              })}
            </div>
            {saveOpen && onSaveView && (
              <div style={{ display: 'flex', gap: 5, marginTop: 8 }}>
                <input autoFocus value={saveName} onChange={e => setSaveName(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter' && saveName.trim()) { onSaveView(saveName.trim(), false); setSaveName(''); setSaveOpen(false); } if (e.key === 'Escape') { setSaveName(''); setSaveOpen(false); } }}
                  placeholder="Name this view" style={{ ...numIn, flex: 1, width: 'auto' }} />
                <button onClick={() => { if (saveName.trim()) { onSaveView(saveName.trim(), false); setSaveName(''); setSaveOpen(false); } }}
                  style={{ ...ghostBtn, background: CRIMSON, color: '#fff', border: 'none' }}>Save</button>
              </div>
            )}
          </div>
        )}

        {/* sections */}
        <div style={{ maxHeight: 'calc(100vh - 220px)', overflowY: 'auto' }}>
          {schema.groups.map(g => {
            const groupFields = schema.fields.filter(f => f.group === g);
            const isOpen = open[g];
            const visible = showAll[g] ? groupFields : groupFields.slice(0, PRIMARY_PER_GROUP);
            const activeInGroup = groupFields.filter(f => leaves.some(l => l.field === f.key)).length;
            return (
              <div key={g} style={{ borderBottom: `0.5px solid ${HAIR}` }}>
                <button onClick={() => setOpen(o => ({ ...o, [g]: !o[g] }))}
                  style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0.6rem 0.85rem', background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left' }}>
                  <span style={{ fontSize: '0.72rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: INK }}>
                    {g}{activeInGroup > 0 && <span style={{ color: CRIMSON, marginLeft: 6 }}>· {activeInGroup}</span>}
                  </span>
                  <span style={{ color: MUTE, fontSize: '0.7rem', transform: isOpen ? 'rotate(90deg)' : 'none', transition: 'transform 0.15s' }}>▸</span>
                </button>
                {isOpen && (
                  <div style={{ padding: '0 0.85rem 0.7rem' }}>
                    {visible.map(f => (
                      <div key={f.key} style={{ padding: '0.4rem 0' }}>
                        <div style={{ fontSize: '0.7rem', color: MUTE, marginBottom: 5 }}>{f.label}</div>
                        {control(f)}
                      </div>
                    ))}
                    {groupFields.length > PRIMARY_PER_GROUP && (
                      <button onClick={() => setShowAll(s => ({ ...s, [g]: !s[g] }))} style={{ ...ghostBtn, marginTop: 4, color: CRIMSON, padding: '0.2rem 0' }}>
                        {showAll[g] ? 'Show less' : `Show ${groupFields.length - PRIMARY_PER_GROUP} more`}
                      </button>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

const numIn = { fontSize: '0.78rem', color: INK, background: '#fff', border: `0.5px solid ${HAIR}`, borderRadius: 7, padding: '0.3rem 0.45rem', width: 56, outline: 'none', boxSizing: 'border-box' };
const ghostBtn = { fontSize: '0.74rem', fontWeight: 500, color: '#4A5568', background: 'none', border: 'none', borderRadius: 7, padding: '0.25rem 0.5rem', cursor: 'pointer' };
function seg(active) { return { fontSize: '0.74rem', fontWeight: 500, color: active ? '#fff' : '#4A5568', background: active ? CRIMSON : '#fff', border: `0.5px solid ${active ? CRIMSON : HAIR}`, borderRadius: 7, padding: '0.25rem 0.6rem', cursor: 'pointer', whiteSpace: 'nowrap' }; }
